import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { ELoadStatus } from '../../../../state/state.interface';
import { DateUtilitiesService } from '../../../../utilities/date-utilities.service';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../utilities/http-utilities.service';
import { RecurringItemStore } from '../../items-modal/recurring-item.store';
import { EWorkflow, IItemJoins } from '../../items.interface';
import { IKamishibaiItem } from './items-kamishibai.interface';

export interface IKamishibaiViewComponentState {
  dataLoading: ELoadStatus;
  data: IItemJoins<string>[];
  dataTotal: number;
}

@Injectable()
export class IKamishibaiViewStore extends ComponentStore<IKamishibaiViewComponentState> {
  public readonly data$ = this.select((state: IKamishibaiViewComponentState) => state.data);
  public readonly dataStatus$ = this.select((state: IKamishibaiViewComponentState) => state.dataLoading);

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getItems(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IItemJoins<string>>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            () => {
              this.patchState({ dataLoading: ELoadStatus.failure });
            },
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
    });
  }

  private getItems(params: HttpParams): Observable<IGetManyResponse<IItemJoins<string>>> {
    return this.http.get<IGetManyResponse<IItemJoins<string>>>('items', { params });
  }

  public formatData(data: IItemJoins<string>): IKamishibaiItem | null {
    const { dueDate } = data;
    const status: EWorkflow = data?.currentWorkflowStep?.statusCategory || EWorkflow.todo;
    const cron: string | undefined = data?.itemRecurrence?.cron;

    if (!dueDate) {
      return null;
    }

    const isOverDue =
      status !== EWorkflow.done &&
      DateUtilitiesService.getNowInUTC()
        .startOf('day')
        .diff(DateUtilitiesService.buildDayjsFromISO8601DateString(dueDate).startOf('day'), 'day') > 0;

    return {
      data: {
        assigneeName: data.assignee?.name || null,
        boardId: data?.board?.id,
        boardKey: data.board?.key ?? '',
        dueDate,
        id: data?.id,
        isOverDue,
        itemKey: data?.key,
        status,
        title: data?.name,
      },
      date: DateUtilitiesService.convertUTCToUserFormatted(dueDate, false, 'YYYY-MM-DD'),
      group: cron ? RecurringItemStore.getCronSchedule(cron) : 'daily',
    };
  }
}
